// batch-37 schadeformulier (FO-326) — Europees aanrijdingsformulier digitaal.
//
// Eén gedeelde bron voor: de statussen, de officiële formulierteksten (17
// toedracht-vakjes, disclaimers), de Zod-contracten voor de JSON-blokken
// (partij A/B, blad 2) en de request-schemas die de portal-wizard, de
// backoffice én de API delen (CLAUDE.md "Validatie & foutmeldingen": één
// gedeeld schema, client valideert vóór verzenden, API parse't hetzelfde).
//
// De formulierteksten zijn letterlijk overgenomen van het officiële formulier
// (Verbond van Verzekeraars/CEA) en bewust i18n-onafhankelijke NL-constanten:
// het is een NL/EU-standaarddocument.

import { z } from 'zod'
import { queryBoolean } from './zod-query'

// ─── Statussen ──────────────────────────────────────────────────────────────

export const SCHADE_MELDING_STATUS = [
  'GEMELD',
  'AANGEVULD',
  'VERZONDEN',
  'IN_BEHANDELING',
  'AFGEHANDELD',
] as const
export type SchadeMeldingStatus = (typeof SCHADE_MELDING_STATUS)[number]

export const SCHADE_MELDING_STATUS_LABELS: Record<SchadeMeldingStatus, string> = {
  GEMELD: 'Gemeld',
  AANGEVULD: 'Aangevuld',
  VERZONDEN: 'Verzonden',
  IN_BEHANDELING: 'In behandeling',
  AFGEHANDELD: 'Afgehandeld',
}

/** Badge-varianten voor de gedeelde Badge-component. */
export const SCHADE_MELDING_STATUS_BADGE: Record<
  SchadeMeldingStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  GEMELD: 'destructive',
  AANGEVULD: 'default',
  VERZONDEN: 'secondary',
  IN_BEHANDELING: 'secondary',
  AFGEHANDELD: 'outline',
}

/** Toegestane statusovergangen (backoffice-flow, FO-326 §2.2). */
export const SCHADE_MELDING_STATUS_OVERGANGEN: Record<SchadeMeldingStatus, SchadeMeldingStatus[]> =
  {
    GEMELD: ['AANGEVULD'],
    AANGEVULD: ['VERZONDEN'],
    VERZONDEN: ['IN_BEHANDELING', 'AFGEHANDELD'],
    IN_BEHANDELING: ['AFGEHANDELD'],
    AFGEHANDELD: [],
  }

export const SCHADE_PARTIJ_B_TYPE = ['VOERTUIG', 'EENZIJDIG', 'DOORRIJDER'] as const
export type SchadePartijBType = (typeof SCHADE_PARTIJ_B_TYPE)[number]

export const SCHADE_PARTIJ_B_TYPE_LABELS: Record<SchadePartijBType, string> = {
  VOERTUIG: 'Tegenpartij met voertuig',
  EENZIJDIG: 'Eenzijdig (object/geen tegenpartij)',
  DOORRIJDER: 'Doorrijder (tegenpartij onbekend)',
}

export const SCHADE_MELDING_AUDIT_ACTIE = [
  'AANGEMAAKT',
  'BIJGEWERKT',
  'ONDERTEKEND',
  'BEVROREN',
  'CORRECTIE_TOEGEVOEGD',
  'BLAD2_AANGEVULD',
  'STATUS_GEWIJZIGD',
  'VERZONDEN',
  'SCHADENUMMERS_GEWIJZIGD',
  'FOTO_TOEGEVOEGD',
  'PDF_GEGENEREERD',
  'PDF_GEDOWNLOAD',
  'ONGEVAL_GEKOPPELD',
  'VERWIJDERD',
] as const
export type SchadeMeldingAuditActie = (typeof SCHADE_MELDING_AUDIT_ACTIE)[number]

export const SCHADE_MELDING_AUDIT_ACTIE_LABELS: Record<SchadeMeldingAuditActie, string> = {
  AANGEMAAKT: 'Aangemaakt',
  BIJGEWERKT: 'Bijgewerkt',
  ONDERTEKEND: 'Ondertekend',
  BEVROREN: 'Blad 1 bevroren',
  CORRECTIE_TOEGEVOEGD: 'Correctie toegevoegd',
  BLAD2_AANGEVULD: 'Blad 2 aangevuld',
  STATUS_GEWIJZIGD: 'Status gewijzigd',
  VERZONDEN: 'Verzonden aan verzekeraar',
  SCHADENUMMERS_GEWIJZIGD: 'Schadenummers gewijzigd',
  FOTO_TOEGEVOEGD: 'Foto toegevoegd',
  PDF_GEGENEREERD: 'PDF gegenereerd',
  PDF_GEDOWNLOAD: 'PDF gedownload',
  ONGEVAL_GEKOPPELD: 'Ongevallenregistratie gekoppeld',
  VERWIJDERD: 'Verwijderd',
}

// ─── Officiële formulierteksten (letterlijk van het formulier) ──────────────

/**
 * Vak 12 — de 17 officiële toedracht-vakjes. De teksten zijn letterlijk
 * overgenomen van het formulier (incl. de `*`-doorhaal-varianten van vak 1/2).
 * Het nummer (1-17) is de waarde die in `toedrachtA`/`toedrachtB` wordt
 * opgeslagen en op de PDF wordt aangekruist.
 */
export const SCHADE_TOEDRACHT_OPTIES: ReadonlyArray<{ nr: number; tekst: string }> = [
  { nr: 1, tekst: 'stond geparkeerd / stond stil' },
  { nr: 2, tekst: 'verliet een parkeerplaats / opende de deur' },
  { nr: 3, tekst: 'ging parkeren' },
  { nr: 4, tekst: 'reed weg van een parkeerplaats, een uitrit, een onverharde weg' },
  { nr: 5, tekst: 'was bezig een parkeerplaats, een inrit, een onverharde weg op te rijden' },
  { nr: 6, tekst: 'wilde een rotonde oprijden' },
  { nr: 7, tekst: 'reed op een rotonde' },
  { nr: 8, tekst: 'botste op achterzijde, in dezelfde richting en op dezelfde rijstrook rijdend' },
  { nr: 9, tekst: 'reed in dezelfde richting en op een andere rijstrook' },
  { nr: 10, tekst: 'veranderde van rijstrook' },
  { nr: 11, tekst: 'haalde in' },
  { nr: 12, tekst: 'ging rechtsaf' },
  { nr: 13, tekst: 'ging linksaf' },
  { nr: 14, tekst: 'reed achteruit' },
  { nr: 15, tekst: 'kwam op een rijbaan bestemd voor het tegemoetkomend verkeer' },
  { nr: 16, tekst: 'kwam van rechts (op een kruising)' },
  { nr: 17, tekst: 'lette niet op een voorrangsteken of een rood licht' },
]

/** Vak 12 onderaan: "vermeld het aantal aangekruiste vakjes". */
export const SCHADE_TOEDRACHT_TELLING_TEKST = 'vermeld het aantal aangekruiste vakjes'

/** Vak 15 — officiële disclaimer boven de handtekeningen. */
export const SCHADE_ONDERTEKENING_DISCLAIMER =
  'Houdt geen erkenning van aansprakelijkheid in; dient uitsluitend voor het vaststellen van identiteit en feitelijke gegevens; bevordert snelle schadeafhandeling.'

export const SCHADE_ONDERTEKENING_KOP = 'Te ondertekenen door BEIDE bestuurders'

/** Onderrand blad 1 — de bevriezings-regel van het papieren formulier. */
export const SCHADE_BEVRIEZING_TEKST =
  'Na ondertekening door beide partijen en na scheiding van de twee formulieren niets meer veranderen.'

/** Vak 13 — officiële schets-instructie. */
export const SCHADE_SCHETS_INSTRUCTIE =
  'Goed aangeven: 1. wegsituatie - 2. rijrichting van voertuigen A en B - 3. hun positie op het moment der botsing - 4. verkeerstekens - 5. straatnamen (of wegen)'

/** Vak 10 — raakpunt-instructie. */
export const SCHADE_RAAKPUNT_INSTRUCTIE =
  'Geef aan waar het voertuig het eerst werd geraakt.'

// ─── Raakpunt-silhouetten ───────────────────────────────────────────────────

export const SCHADE_SILHOUET = ['AUTO', 'AANHANGER', 'MOTOR'] as const
export type SchadeSilhouet = (typeof SCHADE_SILHOUET)[number]

export const SCHADE_SILHOUET_LABELS: Record<SchadeSilhouet, string> = {
  AUTO: 'Auto/bestelbus',
  AANHANGER: 'Aanhanger',
  MOTOR: 'Motor',
}

export const SchadeRaakpuntSchema = z.object({
  silhouet: z.enum(SCHADE_SILHOUET),
  /** Relatieve positie op het silhouet, 0..1 (x = links→rechts, y = boven→onder). */
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
})
export type SchadeRaakpunt = z.infer<typeof SchadeRaakpuntSchema>

// ─── Getuigen ───────────────────────────────────────────────────────────────

export const SchadeGetuigeSchema = z.object({
  naam: z.string().trim().min(1, 'Naam is verplicht').max(200),
  adresPlaats: z.string().trim().max(300).nullable().default(null),
  telefoon: z.string().trim().max(50).nullable().default(null),
})
export type SchadeGetuige = z.infer<typeof SchadeGetuigeSchema>

// ─── Partijblokken (vak 6-9, gestructureerd conform het formulier) ──────────

// Vrij-tekst-string die ook leeg/null mag zijn: de tegenpartij-gegevens worden
// op straat overgetypt; niets afdwingen behalve maximale lengte.
const veld = (max = 200) => z.string().trim().max(max).nullable().default(null)

export const SchadePartijVerzekeringnemerSchema = z.object({
  naam: veld(),
  voornaam: veld(),
  adres: veld(300),
  postcode: veld(20),
  plaats: veld(),
  land: veld(100),
  telEmail: veld(),
})

export const SchadePartijVoertuigSchema = z.object({
  merkType: veld(),
  kenteken: veld(30),
  land: veld(100),
})

export const SchadePartijAanhangerSchema = z.object({
  kenteken: veld(30),
  land: veld(100),
})

export const SchadePartijVerzekeringSchema = z.object({
  maatschappij: veld(),
  polisNummer: veld(100),
  groeneKaartNummer: veld(100),
  /** Vrije datumtekst (dd-mm-jjjj) — overgetypt van de groene kaart. */
  geldigVanaf: veld(30),
  geldigTot: veld(30),
  agentschap: veld(300),
  telEmail: veld(),
  allRisks: z.boolean().nullable().default(null),
})

export const SchadePartijBestuurderSchema = z.object({
  naam: veld(),
  voornaam: veld(),
  /** Vrije datumtekst (dd-mm-jjjj). */
  geboortedatum: veld(30),
  adres: veld(300),
  postcode: veld(20),
  plaats: veld(),
  land: veld(100),
  telEmail: veld(),
  rijbewijsNummer: veld(50),
  rijbewijsCategorie: veld(30),
  /** Vrije datumtekst (dd-mm-jjjj). */
  rijbewijsGeldigTot: veld(30),
})

export const SchadePartijSchema = z.object({
  verzekeringnemer: SchadePartijVerzekeringnemerSchema,
  voertuig: SchadePartijVoertuigSchema,
  aanhanger: SchadePartijAanhangerSchema.nullable().default(null),
  verzekering: SchadePartijVerzekeringSchema,
  bestuurder: SchadePartijBestuurderSchema,
})
export type SchadePartij = z.infer<typeof SchadePartijSchema>

/** Lege partij (alle velden null) — startpunt voor partij B in de wizard. */
export function legeSchadePartij(): SchadePartij {
  return SchadePartijSchema.parse({
    verzekeringnemer: {},
    voertuig: {},
    aanhanger: null,
    verzekering: {},
    bestuurder: {},
  })
}

// ─── Correctieblok (na bevriezing) ──────────────────────────────────────────

export const SchadeCorrectieSchema = z.object({
  tekst: z.string().trim().min(1).max(2000),
  doorUserId: z.string(),
  doorNaam: z.string().max(200),
  /** ISO-datetime van de correctie. */
  op: z.string(),
})
export type SchadeCorrectie = z.infer<typeof SchadeCorrectieSchema>

// ─── Blad 2 (achterzijde) ───────────────────────────────────────────────────
//
// Doorhaal-keuzes van het papieren formulier zijn expliciete selects (FO-326
// §5): ja/nee = boolean, meerkeuze = enum. `null` = nog niet ingevuld.

export const SCHADE_DIENSTVERBAND = ['LOONDIENST', 'ZELFSTANDIG'] as const
export type SchadeDienstverband = (typeof SCHADE_DIENSTVERBAND)[number]
export const SCHADE_DIENSTVERBAND_LABELS: Record<SchadeDienstverband, string> = {
  LOONDIENST: 'Loondienst',
  ZELFSTANDIG: 'Zelfstandig',
}

export const SCHADE_GESLACHT = ['M', 'V'] as const
export type SchadeGeslacht = (typeof SCHADE_GESLACHT)[number]

export const SCHADE_GEBRUIK = ['BEDRIJF', 'BEROEP', 'VAKANTIE', 'OVERIG'] as const
export type SchadeGebruik = (typeof SCHADE_GEBRUIK)[number]
export const SCHADE_GEBRUIK_LABELS: Record<SchadeGebruik, string> = {
  BEDRIJF: 'Bedrijf',
  BEROEP: 'Beroep',
  VAKANTIE: 'Vakantie',
  OVERIG: 'Overig particulier gebruik',
}

export const SCHADE_BIJZONDER_GEBRUIK = [
  'GEEN',
  'VERHUUR_ZONDER_CHAUFFEUR',
  'TAXI',
  'GOEDERENVERVOER_DERDEN',
  'LESRIJDEN',
] as const
export type SchadeBijzonderGebruik = (typeof SCHADE_BIJZONDER_GEBRUIK)[number]
export const SCHADE_BIJZONDER_GEBRUIK_LABELS: Record<SchadeBijzonderGebruik, string> = {
  GEEN: 'Geen',
  VERHUUR_ZONDER_CHAUFFEUR: 'Verhuur zonder chauffeur',
  TAXI: 'Taxi',
  GOEDERENVERVOER_DERDEN: 'Goederenvervoer voor derden',
  LESRIJDEN: 'Lesrijden',
}

export const SCHADE_POLITIE_RAPPORT = ['JA', 'NEE', 'ONBEKEND'] as const
export type SchadePolitieRapport = (typeof SCHADE_POLITIE_RAPPORT)[number]
export const SCHADE_POLITIE_RAPPORT_LABELS: Record<SchadePolitieRapport, string> = {
  JA: 'Ja',
  NEE: 'Nee',
  ONBEKEND: 'Onbekend',
}

export const SCHADE_VERLICHTING = ['STADSLICHT', 'DIMLICHT', 'GROOT_LICHT'] as const
export type SchadeVerlichting = (typeof SCHADE_VERLICHTING)[number]
export const SCHADE_VERLICHTING_LABELS: Record<SchadeVerlichting, string> = {
  STADSLICHT: 'Stadslicht',
  DIMLICHT: 'Dimlicht',
  GROOT_LICHT: 'Groot licht',
}

export const SCHADE_UITZICHT = ['VRIJ', 'BELEMMERD'] as const
export type SchadeUitzicht = (typeof SCHADE_UITZICHT)[number]

export const SCHADE_WEGDEK = ['DROOG', 'NAT', 'SNEEUW', 'HAGEL', 'IJZEL'] as const
export type SchadeWegdek = (typeof SCHADE_WEGDEK)[number]
export const SCHADE_WEGDEK_LABELS: Record<SchadeWegdek, string> = {
  DROOG: 'Droog',
  NAT: 'Nat',
  SNEEUW: 'Sneeuw',
  HAGEL: 'Hagel',
  IJZEL: 'IJzel',
}

export const SCHADE_SLACHTOFFER_CATEGORIE = ['EIGEN_VOERTUIG', 'TEGENPARTIJ'] as const
export type SchadeSlachtofferCategorie = (typeof SCHADE_SLACHTOFFER_CATEGORIE)[number]
export const SCHADE_SLACHTOFFER_CATEGORIE_LABELS: Record<SchadeSlachtofferCategorie, string> = {
  EIGEN_VOERTUIG: 'Eigen voertuig',
  TEGENPARTIJ: 'Tegenpartij',
}

export const SchadeSlachtofferSchema = z.object({
  categorie: z.enum(SCHADE_SLACHTOFFER_CATEGORIE),
  naam: veld(),
  postcodeHuisnummer: veld(50),
  geslacht: z.enum(SCHADE_GESLACHT).nullable().default(null),
  geboortedatum: veld(30),
  aardLetsel: veld(300),
})
export type SchadeSlachtoffer = z.infer<typeof SchadeSlachtofferSchema>

/** Omstandigheden-kolom (per partij: Verzekerde / Tegenpartij). */
export const SchadeOmstandighedenKolomSchema = z.object({
  snelheidKmU: veld(20),
  maxSnelheidKmU: veld(20),
  wegType: veld(100), // voorrangs-, verharde, onverharde weg of fietspad
  positieOpWeg: veld(100), // rechts, links of midden
  aantalRijstroken: veld(20),
  ononderbrokenStreep: veld(100),
  wegBreedte: veld(50),
  richtingAangegeven: veld(100),
  drankOfMiddelen: veld(200),
  gordelBestuurder: z.boolean().nullable().default(null),
  gordelPassagier: z.boolean().nullable().default(null),
  verlichtingGevoerd: z.boolean().nullable().default(null),
  verlichtingSoort: z.enum(SCHADE_VERLICHTING).nullable().default(null),
  uitzicht: z.enum(SCHADE_UITZICHT).nullable().default(null),
  uitzichtToelichting: veld(200),
  wegdek: z.enum(SCHADE_WEGDEK).nullable().default(null),
})
export type SchadeOmstandighedenKolom = z.infer<typeof SchadeOmstandighedenKolomSchema>

export const SchadeBlad2Schema = z.object({
  verzekeringnemer: z
    .object({
      iban: veld(50),
      beroep: veld(),
      dienstverband: z.enum(SCHADE_DIENSTVERBAND).nullable().default(null),
      rechtOpBtwAftrek: z.boolean().nullable().default(null),
    })
    .prefault({}),
  bestuurder: z
    .object({
      geboortedatum: veld(30),
      geslacht: z.enum(SCHADE_GESLACHT).nullable().default(null),
      gemachtigd: z.boolean().nullable().default(null),
      nietGemachtigdWaarom: veld(300),
    })
    .prefault({}),
  motorrijtuig: z
    .object({
      kmStand: veld(20),
      gebruikTijdensVoorval: z.enum(SCHADE_GEBRUIK).nullable().default(null),
      bijzonderGebruik: z.enum(SCHADE_BIJZONDER_GEBRUIK).nullable().default(null),
      aanhangerGekoppeld: z.boolean().nullable().default(null),
      chassisnummerOngekentekend: veld(50),
    })
    .prefault({}),
  schade: z
    .object({
      /** Decimaal als string ("1250,00" of "1250.00") — schatting mag. */
      geschatBedrag: veld(30),
      geschatDoor: veld(),
      reparateurNaamAdres: veld(300),
      reparateurTelefoon: veld(50),
      wanneerNaarReparateur: veld(),
    })
    .prefault({}),
  rechtsbijstand: z
    .object({
      verzekerd: z.boolean().nullable().default(null),
      polisNummer: veld(100),
      maatschappij: veld(),
      andereSchadeDanMotorrijtuig: z.boolean().nullable().default(null),
      andereSchadeWelke: veld(300),
    })
    .prefault({}),
  inzittenden: z
    .object({
      verzekerd: z.boolean().nullable().default(null),
      polisNummer: veld(100),
      maatschappij: veld(),
    })
    .prefault({}),
  politie: z
    .object({
      aanwezigGeweest: z.boolean().nullable().default(null),
      plaats: veld(),
      geholpenBijInvullen: z.boolean().nullable().default(null),
      apartRapport: z.enum(SCHADE_POLITIE_RAPPORT).nullable().default(null),
    })
    .prefault({}),
  slachtoffers: z.array(SchadeSlachtofferSchema).max(8).default([]),
  omstandigheden: z
    .object({
      binnenBebouwdeKom: z.boolean().nullable().default(null),
      verzekerde: SchadeOmstandighedenKolomSchema.prefault({}),
      tegenpartij: SchadeOmstandighedenKolomSchema.prefault({}),
    })
    .prefault({}),
  aansprakelijkheid: z
    .object({
      visie: veld(2000),
      beschiktOverAanrijdingsformulier: z.boolean().nullable().default(null),
    })
    .prefault({}),
  verklaring: z
    .object({
      /** Vrije datumtekst (dd-mm-jjjj) van ondertekening. */
      datum: veld(30),
      ondertekenaarNaam: veld(),
    })
    .prefault({}),
})
export type SchadeBlad2 = z.infer<typeof SchadeBlad2Schema>

/** Leeg blad 2 (alle velden null/leeg) — startpunt vóór aanvulling. */
export function leegSchadeBlad2(): SchadeBlad2 {
  return SchadeBlad2Schema.parse({})
}

// ─── Request-schemas ────────────────────────────────────────────────────────

/** Base64 PNG-data-url (handtekening/schets uit canvas). */
const pngDataUrl = z
  .string()
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Ongeldige afbeelding')
  .max(4_000_000, 'Afbeelding te groot')

/**
 * Blad 1 aanmaken (portal-wizard, einde stap 8). Minimum conform FO-326 §5:
 * datum/tijd, locatie, partij A, kenteken B (of expliciet eenzijdig/doorrijder),
 * minstens toedracht óf een opmerking, handtekening A. Gewonden blokkeert nooit.
 */
export const CreateSchadeMeldingSchema = z
  .object({
    /** ISO-datetime van de aanrijding. */
    datumTijd: z.string().datetime({ offset: true }),
    locatieLand: veld(100),
    locatiePlaats: z.string().trim().min(1, 'Plaats is verplicht').max(200),
    locatieStraat: veld(),
    locatieGps: veld(100),
    gewonden: z.boolean().default(false),
    materieleSchadeAndereVoertuigen: z.boolean().default(false),
    materieleSchadeAndereObjecten: z.boolean().default(false),
    getuigen: z.array(SchadeGetuigeSchema).max(10).default([]),

    partijA: SchadePartijSchema,
    partijBType: z.enum(SCHADE_PARTIJ_B_TYPE).default('VOERTUIG'),
    partijB: SchadePartijSchema.nullable().default(null),

    toedrachtA: z.array(z.number().int().min(1).max(17)).max(17).default([]),
    toedrachtB: z.array(z.number().int().min(1).max(17)).max(17).default([]),
    raakpuntA: SchadeRaakpuntSchema.nullable().default(null),
    raakpuntB: SchadeRaakpuntSchema.nullable().default(null),
    schadeTekstA: veld(500),
    schadeTekstB: veld(500),
    opmerkingenA: veld(2000),
    opmerkingenB: veld(2000),

    /** Canvas-schets als PNG-data-url (vak 13); foto-fallback gaat via de foto-upload. */
    schetsDataUrl: pngDataUrl.nullable().default(null),

    handtekeningA: z.object({
      dataUrl: pngDataUrl,
      naam: z.string().trim().min(1, 'Naam ondertekenaar is verplicht').max(200),
    }),
    handtekeningB: z
      .object({
        dataUrl: pngDataUrl,
        naam: z.string().trim().min(1, 'Naam ondertekenaar is verplicht').max(200),
      })
      .nullable()
      .default(null),
    /** Weiger-pad (FO-326 §2.1): tegenpartij tekende niet digitaal. */
    tegenpartijWeigerde: z.boolean().default(false),

    /** Afwijkende bestuurder (FO-326 §6): meldt expliciet dat de melder niet de toegewezen bestuurder is. */
    afwijkendeBestuurder: z.boolean().default(false),

    /** FO-307-A1 — idempotency-key per melding (offline-replays zijn no-ops). */
    idempotencyKey: z.string().trim().min(8).max(100).nullable().default(null),
  })
  .superRefine((data, ctx) => {
    // Kenteken B verplicht bij een normale tegenpartij (FO-326 §5).
    if (data.partijBType === 'VOERTUIG') {
      const kentekenB = data.partijB?.voertuig.kenteken
      if (!kentekenB || kentekenB.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['partijB', 'voertuig', 'kenteken'],
          message: 'Kenteken tegenpartij is verplicht (of kies eenzijdig/doorrijder)',
        })
      }
      // Raakpunt verplicht per betrokken voertuig.
      if (!data.raakpuntB) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['raakpuntB'],
          message: 'Tik het eerste raakpunt van voertuig B aan',
        })
      }
    }
    if (!data.raakpuntA) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['raakpuntA'],
        message: 'Tik het eerste raakpunt van je voertuig aan',
      })
    }
    // Minstens de toedracht óf een opmerking (FO-326 §5).
    if (data.toedrachtA.length === 0 && data.toedrachtB.length === 0) {
      const opmerking = (data.opmerkingenA ?? '').trim()
      if (opmerking.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['toedrachtA'],
          message: 'Kruis minstens één toedracht-vakje aan of vul een opmerking in',
        })
      }
    }
  })
export type CreateSchadeMeldingInput = z.infer<typeof CreateSchadeMeldingSchema>

/** Blad 2 aanvullen/bijwerken (portal eigen melding + backoffice). */
export const UpdateSchadeBlad2Schema = z.object({
  blad2: SchadeBlad2Schema,
})
export type UpdateSchadeBlad2Input = z.infer<typeof UpdateSchadeBlad2Schema>

/** Correctie toevoegen ná bevriezing (append-only correctieblok). */
export const AddSchadeCorrectieSchema = z.object({
  tekst: z.string().trim().min(1, 'Vul de correctie in').max(2000),
})
export type AddSchadeCorrectieInput = z.infer<typeof AddSchadeCorrectieSchema>

/** Statusovergang (backoffice): IN_BEHANDELING of AFGEHANDELD (+ uitkomst). */
export const UpdateSchadeStatusSchema = z
  .object({
    status: z.enum(['IN_BEHANDELING', 'AFGEHANDELD']),
    uitkomst: veld(2000),
    /** Decimaal als string, bv. "1250.00". */
    bedragUitgekeerd: z
      .string()
      .trim()
      .regex(/^\d{1,10}([.,]\d{1,2})?$/, 'Ongeldig bedrag')
      .nullable()
      .default(null),
    bedragEigenRisico: z
      .string()
      .trim()
      .regex(/^\d{1,10}([.,]\d{1,2})?$/, 'Ongeldig bedrag')
      .nullable()
      .default(null),
  })
  .superRefine((data, ctx) => {
    if (data.status === 'AFGEHANDELD' && !(data.uitkomst ?? '').trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['uitkomst'],
        message: 'Vul de uitkomst in bij afhandelen',
      })
    }
  })
export type UpdateSchadeStatusInput = z.infer<typeof UpdateSchadeStatusSchema>

/** Schadenummers registreren (backoffice, FO-326 §2.4). */
export const UpdateSchadenummersSchema = z.object({
  schadenummerTussenpersoon: veld(100),
  schadenummerMaatschappij: veld(100),
})
export type UpdateSchadenummersInput = z.infer<typeof UpdateSchadenummersSchema>

/** Verzekeraarsmail: concept opvragen + verzenden (FO-150-patroon). */
export const SchadeVerzekeraarMailSchema = z.object({
  aan: z.string().trim().email('Ongeldig mailadres'),
  cc: z.array(z.string().trim().email('Ongeldig mailadres')).max(10).default([]),
  onderwerp: z.string().trim().min(1, 'Onderwerp is verplicht').max(300),
  bericht: z.string().trim().min(1, 'Bericht is verplicht').max(10000),
})
export type SchadeVerzekeraarMailInput = z.infer<typeof SchadeVerzekeraarMailSchema>

/** Lijst-query (backoffice, FO-155-paginering). */
export const ListSchadeMeldingenQuerySchema = z.object({
  status: z.enum(SCHADE_MELDING_STATUS).optional(),
  voertuigId: z.string().optional(),
  bestuurderEmployeeId: z.string().optional(),
  gewonden: queryBoolean().optional(),
  zoek: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type ListSchadeMeldingenQuery = z.infer<typeof ListSchadeMeldingenQuerySchema>

/**
 * Instellingen (backoffice): alleen nog de blad-2-herinnering. FO-330: het
 * per-BV verzekeraars-blok (UpsertSchadeVerzekeraarInstellingSchema +
 * SchadeVerzekeraarInstellingDTO) is VERWIJDERD — verzekeraarsgegevens staan
 * uitsluitend op het voertuig (createVoertuigSchema in voertuigen.ts).
 */
export const UpdateSchadeInstellingenSchema = z.object({
  blad2HerinneringDagen: z.coerce.number().int().min(1).max(60),
})
export type UpdateSchadeInstellingenInput = z.infer<typeof UpdateSchadeInstellingenSchema>

// ─── Verzenden-validatie (gedeeld client + server) ──────────────────────────

/**
 * Verplichte punten vóór VERZONDEN (FO-326 §5/§6): schets of schets-foto,
 * blad-2-kernvelden, en bij een doorrijder het politie-blok. Pure functie,
 * gedeeld door de backoffice-UI (inline tonen) en de API (afdwingen).
 * Retourneert een lijst mensleesbare gebreken; leeg = verzendbaar.
 */
export function valideerSchadeVoorVerzenden(input: {
  partijBType: SchadePartijBType
  schetsAanwezig: boolean
  schetsFotoAanwezig: boolean
  blad2: SchadeBlad2 | null
}): string[] {
  const gebreken: string[] = []
  if (!input.schetsAanwezig && !input.schetsFotoAanwezig) {
    gebreken.push('Situatieschets (of foto van een papieren schets) ontbreekt')
  }
  const b2 = input.blad2
  if (!b2) {
    gebreken.push('Blad 2 (achterzijde) is nog niet ingevuld')
    return gebreken
  }
  if (!b2.motorrijtuig.kmStand?.trim()) gebreken.push('Blad 2: km-stand ontbreekt')
  if (!b2.motorrijtuig.gebruikTijdensVoorval) {
    gebreken.push('Blad 2: gebruik tijdens voorval ontbreekt')
  }
  if (b2.bestuurder.gemachtigd == null) {
    gebreken.push('Blad 2: "was de bestuurder gemachtigd te rijden" ontbreekt')
  }
  if (!b2.schade.geschatBedrag?.trim()) {
    gebreken.push('Blad 2: geschat schadebedrag ontbreekt (schatting mag)')
  }
  if (!b2.aansprakelijkheid.visie?.trim()) {
    gebreken.push('Blad 2: aansprakelijkheidsvisie ontbreekt')
  }
  if (input.partijBType === 'DOORRIJDER') {
    if (b2.politie.aanwezigGeweest == null) {
      gebreken.push('Doorrijder: politie-blok is verplicht (aanwezig geweest ja/nee)')
    }
  }
  return gebreken
}

// ─── DTO's ──────────────────────────────────────────────────────────────────

/** Backoffice-lijstrij (FO-326 §2.4-kolommen). Bewust zónder partij-blokken. */
export interface SchadeMeldingListRowDTO {
  id: string
  meldingNummer: string
  status: SchadeMeldingStatus
  datumTijd: string
  locatiePlaats: string | null
  gewonden: boolean
  partijBType: SchadePartijBType
  /** Verzekeringsmaatschappij van de tegenpartij (kolom FO §2.4). */
  tegenpartijVerzekeraar: string | null
  voertuig: { id: string; kenteken: string; merk: string; model: string }
  bestuurderNaam: string
  afwijkendeBestuurder: boolean
  blad2Aangevuld: boolean
  verzondenOp: string | null
  createdAt: string
  /**
   * batch-62 garage-voertuig (FO-462 §6.3, CC.0b) - FORMULIER = aanrijdings-
   * formulier; GARAGEFACTUUR = schadedossier aangemaakt bij het bevestigen van
   * een schadefactuur zonder melding (geen formulier).
   */
  bron: 'FORMULIER' | 'GARAGEFACTUUR'
}

/** Portal-lijstrij (eigen meldingen) — compacte weergave. */
export interface MijnSchadeMeldingRowDTO {
  id: string
  meldingNummer: string
  status: SchadeMeldingStatus
  datumTijd: string
  locatiePlaats: string | null
  gewonden: boolean
  blad2Aangevuld: boolean
  /** True zolang blad 2 leeg is: de portal toont een "vul blad 2 in"-CTA. */
  blad2NodigVanBestuurder: boolean
}

export interface SchadeMeldingAuditDTO {
  id: string
  actie: SchadeMeldingAuditActie
  actor: string
  actorNaam: string | null
  toelichting: string | null
  createdAt: string
}

/**
 * Detail-DTO. Tegenpartij-gegevens (partijB) zijn persoonsgegevens: deze DTO
 * wordt ALLEEN geserveerd aan de eigen bestuurder (portal, eigen melding) en
 * aan backoffice-rollen met VOERTUIGEN-permissie — nooit in de klant-app en
 * nooit aan andere medewerkers (FO-326 §4).
 */
export interface SchadeMeldingDetailDTO {
  id: string
  meldingNummer: string
  status: SchadeMeldingStatus
  datumTijd: string
  locatieLand: string | null
  locatiePlaats: string | null
  locatieStraat: string | null
  locatieGps: string | null
  gewonden: boolean
  materieleSchadeAndereVoertuigen: boolean
  materieleSchadeAndereObjecten: boolean
  getuigen: SchadeGetuige[]

  partijA: SchadePartij
  partijBType: SchadePartijBType
  partijB: SchadePartij | null

  toedrachtA: number[]
  toedrachtB: number[]
  raakpuntA: SchadeRaakpunt | null
  raakpuntB: SchadeRaakpunt | null
  schadeTekstA: string | null
  schadeTekstB: string | null
  schetsAanwezig: boolean
  schetsFotoAanwezig: boolean
  fotoCount: number
  opmerkingenA: string | null
  opmerkingenB: string | null

  handtekeningA: { naam: string; op: string } | null
  handtekeningB: { naam: string; op: string } | null
  tegenpartijWeigerde: boolean
  weigerFotoAanwezig: boolean
  bevroren: boolean
  bevrorenOp: string | null
  correcties: SchadeCorrectie[]

  blad2: SchadeBlad2 | null
  blad2AangevuldOp: string | null

  verzondenOp: string | null
  verzondenAan: string | null
  schadenummerTussenpersoon: string | null
  schadenummerMaatschappij: string | null
  afgehandeldOp: string | null
  uitkomst: string | null
  bedragUitgekeerd: number | null
  bedragEigenRisico: number | null

  voertuig: { id: string; kenteken: string; merk: string; model: string }
  bestuurder: { employeeId: string; naam: string }
  afwijkendeBestuurder: boolean
  ongevalRegistratieId: string | null
  documentId: string | null

  createdAt: string
  updatedAt: string
}

// ─── Voorinvulling (FO-326 §2.0) ────────────────────────────────────────────

/** Eén veld dat niet voorgevuld kon worden omdat de bron in de app ontbreekt. */
export interface SchadeOntbrekendeBronDTO {
  /** Pad in de partij-A-/blad-2-structuur, bv. "verzekering.groeneKaartNummer". */
  veld: string
  /** Mensleesbaar veldlabel, bv. "Groene kaart nr.". */
  label: string
  /** Waar de bron hoort te staan, bv. "Voertuig: groene-kaartnummer". */
  bron: string
  /** Hint voor de bestuurder in de wizard. */
  hint: string
}

/**
 * Voorinvul-DTO — het resultaat van de §2.0-matrix, volledig server-side
 * geresolvet (de wizard rekent zelf niets). `partijA` en `blad2Voorinvul`
 * zijn direct bruikbaar als startwaarden (autofill-stijl, overschrijfbaar).
 */
export interface SchadeVoorinvulDTO {
  voertuig: { id: string; kenteken: string; merk: string; model: string; silhouet: SchadeSilhouet }
  partijA: SchadePartij
  blad2Voorinvul: SchadeBlad2
  /** True wanneer de ingelogde medewerker niet de actieve toewijzing heeft. */
  afwijkendeBestuurder: boolean
  /** Aanhanger actief gekoppeld (blad 2 vraag + vak 7A). */
  aanhangerGekoppeld: boolean
  ontbrekendeBronnen: SchadeOntbrekendeBronDTO[]
  /** FO-330: mailadres verzekeraar/tussenpersoon van het VOERTUIG (conceptmail-ontvanger). */
  verzekeraarMailadres?: string | null
}

// ─── Instellingen-DTO's ─────────────────────────────────────────────────────

export interface SchadeInstellingenDTO {
  blad2HerinneringDagen: number
}

/** Concept van de verzekeraarsmail (preview = exact wat verzonden wordt). */
export interface SchadeVerzekeraarMailConceptDTO {
  aan: string | null
  onderwerp: string
  bericht: string
  pdfBestandsnaam: string
  /** Gebreken die verzending blokkeren (uit `valideerSchadeVoorVerzenden`). */
  gebreken: string[]
}
